import './index.scss';

import { MoonFilled, SunOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@/components/Icon';
import { toggleTheme } from '@/core/redux/themeSettingsSlice';
import { type AppDispatch, type RootState } from '@/store/store';

type ThemeToggleProps = {
  className?: string;
  /** `utility`: borderless button of the workspace utility row, showing the theme it switches to. */
  variant?: 'default' | 'utility';
};

export const ThemeToggle = ({ className, variant = 'default' }: ThemeToggleProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation('common');
  const themeMode = useSelector((state: RootState) => state.theme.themeMode);
  const isDarkTheme = themeMode === 'dark';
  const isUtility = variant === 'utility';
  const themeClassName = [
    'c-theme-toggle',
    isDarkTheme && 'c-theme-toggle-dark',
    isUtility && 'c-theme-toggle--utility',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      aria-label={t(isDarkTheme ? 'common.enable_light_mode' : 'common.enable_dark_mode')}
      aria-pressed={isDarkTheme}
      className={themeClassName}
      onClick={() => dispatch(toggleTheme())}
    >
      {isUtility ? (
        <Icon name={isDarkTheme ? 'sun' : 'moon'} />
      ) : (
        <span className={`c-theme-toggle-icon c-theme-toggle-icon-${themeMode}`} aria-hidden="true">
          {isDarkTheme ? <MoonFilled /> : <SunOutlined />}
        </span>
      )}
    </button>
  );
};
